import { useMaximize } from "./hooks/useMaximize";
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import BulkImport from "./BulkImport";
import "./App.css";

/* ============================================================
   CONSTANTS
============================================================ */
const emptyProduct = {
  name: "",
  selling_price: "",
  low_stock_threshold: "5",
  is_active: true,
  sku_code: "",
  barcode: "", // ← was missing
  category_code: "",
  description: "",
  purchase_unit: "piece",
  sale_unit: "piece",
  units_per_purchase: "1",
  category_id: "",
  shelf_location: "",
};

const emptyCategory = {
  name: "",
  category_code: "",
  icon: "📦",
  color: "#00F3FF",
};

// Same 16-item default set create_store_with_owner used to seed automatically
// (now opt-in — see supabase_create_store_with_owner.sql).
const STARTER_CATEGORIES = [
  { name: "Beverages", icon: "🥤", color: "#32D4E0" },
  { name: "Coffee & Tea", icon: "☕", color: "#FFB340" },
  { name: "Bread & Bakery", icon: "🍞", color: "#FF9F0A" },
  { name: "Dairy & Eggs", icon: "🥛", color: "#32D74B" },
  { name: "Oil & Condiments", icon: "🛢️", color: "#FF6B35" },
  { name: "Dry Goods & Grains", icon: "🌾", color: "#BF8B2E" },
  { name: "Canned & Packaged Foods", icon: "🥫", color: "#8E8E93" },
  { name: "Confectionery & Snacks", icon: "🍬", color: "#FF2D55" },
  { name: "Personal Care", icon: "🧴", color: "#BF5AF2" },
  { name: "Hygiene & Paper Products", icon: "🧻", color: "#5AC8FA" },
  { name: "Baby Care & Diapers", icon: "👶", color: "#FFD60A" },
  { name: "Household & Cleaning", icon: "🧹", color: "#30D158" },
  { name: "Health & Medicine", icon: "🩺", color: "#FF453A" },
  { name: "Frozen & Chilled", icon: "🧊", color: "#64D2FF" },
  { name: "Tobacco", icon: "🚬", color: "#98989F" },
  { name: "Airtime & Services", icon: "📱", color: "#32D4E0" },
];

/* ============================================================
   SHARED STYLES
============================================================ */
const inputStyle = {
  height: "38px",
  background: "var(--elevated)",
  border: "1px solid var(--border)",
  borderRadius: "10px",
  padding: "0 12px",
  color: "var(--text)",
  fontFamily: "var(--font)",
  outline: "none",
  fontSize: "13px",
  width: "100%",
  transition: "border 0.15s",
};

const labelStyle = {
  color: "var(--text2)",
  fontSize: "12px",
  marginBottom: "4px",
  display: "block",
  fontWeight: 500,
};

const groupLabelStyle = {
  gridColumn: "1 / -1",
  marginTop: "16px",
  marginBottom: "2px",
  fontWeight: 700,
  color: "var(--text3)",
  fontSize: "11px",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  paddingTop: "12px",
  borderTop: "1px solid var(--border)",
};

/* ============================================================
   MAIN WRAPPER
============================================================ */
export default function Products({ onClose, onCategoriesChange }) {
  const [tab, setTab] = useState("list");
  const [editingProduct, setEditingProduct] = useState(null); // ← add this
  // Replace the current useEffect + fetch pattern with this:
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const { toggle, overlayClass, panelClass, icon } = useMaximize(true);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return; // prevent double-fetch in StrictMode
    initialized.current = true;
    fetchAll();
  }, []);

  useEffect(() => {
    async function getStoreId() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("store_id")
        .eq("id", user.id)
        .single();
      logSupabaseError("Products: getStoreId", error);
      if (data) setStoreId(data.store_id);
    }
    getStoreId();
  }, []);

  async function fetchAll() {
    setLoading(true);
    // ✅ Parallel fetch — much faster than sequential
    const [prodRes, catRes] = await Promise.all([
      supabase
        .from("products")
        .select(
          "id, name, sku_code, barcode, selling_price, stock_qty, low_stock_threshold, is_active, category_id, shelf_location, categories(name, color, icon, category_code)",
        )
        .order("name"),
      supabase.from("categories").select("*").order("name"),
    ]);
    logSupabaseError("Products: fetchAll products", prodRes.error);
    logSupabaseError("Products: fetchAll categories", catRes.error);
    if (prodRes.data) setProducts(prodRes.data);
    if (catRes.data) setCategories(catRes.data);
    setLoading(false);
  }

  async function fetchProducts() {
    const { data, error } = await supabase
      .from("products")
      .select(
        "id, name, sku_code, barcode, selling_price, stock_qty, low_stock_threshold, is_active, category_id, shelf_location, categories(name, color, icon, category_code)",
      )
      .order("name");
    logSupabaseError("Products: fetchProducts", error);
    if (data) setProducts(data);
  }

  async function fetchCategories() {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name");
    logSupabaseError("Products: fetchCategories", error);
    if (data) setCategories(data);
  }

  function goToEdit(product) {
    setEditingProduct(product);
    setTab("add");
  }

  function goToAdd() {
    setEditingProduct(null);
    setTab("add");
  }

  return (
    <div className={overlayClass}>
      <div className={panelClass} style={{ maxWidth: "980px" }}>
        {/* HEADER */}
        <div className="report-header">
          <div>
            <h2 className="report-title">📦 Product Manager</h2>
            <p className="report-date">
              {products.length} products · {categories.length} categories
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div className="inv-tabs-wrap">
              <div className="inv-tabs">
                <button
                  className={`inv-tab ${tab === "list" ? "active" : ""}`}
                  onClick={() => setTab("list")}
                >
                  📋 Products
                </button>
                <button
                  className={`inv-tab ${tab === "add" ? "active" : ""}`}
                  onClick={goToAdd}
                >
                  ➕ Add / Edit
                </button>
                <button
                  className={`inv-tab ${tab === "categories" ? "active" : ""}`}
                  onClick={() => setTab("categories")}
                >
                  🏷️ Categories
                </button>
              </div>
            </div>
            <button
              className="inv-tab products-bulk-import-btn"
              onClick={() => setShowBulkImport(true)}
              style={{ marginRight: "8px" }}
              title="Bulk Import"
            >
              📥<span className="products-bulk-import-label"> Bulk Import</span>
            </button>
            {/* Maximize button */}
            <button
              className="maximize-btn"
              onClick={toggle}
              title={icon === "⊞" ? "Maximize" : "Restore"}
            >
              {icon}
            </button>
          </div>
          <button className="report-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* TAB CONTENT */}
        {tab === "list" && (
          <ProductList
            products={products}
            categories={categories}
            loading={loading}
            onEdit={goToEdit}
            onRefresh={fetchProducts}
          />
        )}
        {tab === "add" && (
          <ProductForm
            categories={categories}
            products={products}
            onSaved={() => {
              fetchProducts();
              setTab("list");
            }}
            onCancel={() => {
              setEditingProduct(null);
              setTab("list");
            }}
            editingProduct={editingProduct}
            setEditingProduct={setEditingProduct}
          />
        )}
        {tab === "categories" && (
          <CategoriesTab
            categories={categories}
            onRefresh={() => {
              fetchCategories();
              fetchProducts(); // ← refresh products to update category joins
              onCategoriesChange?.();
            }}
          />
        )}
        {showBulkImport && (
          <BulkImport
            categories={categories}
            storeId={storeId}
            onDone={() => {
              setShowBulkImport(false);
              onClose();
            }}
            onClose={() => setShowBulkImport(false)}
          />
        )}
      </div>
    </div>
  );
}

/* ============================================================
   PRODUCT LIST TAB
============================================================ */
function ProductList({ products, categories, loading, onEdit, onRefresh }) {
  const [search, setSearch] = useState("");

  // ✅ Search by name, SKU and barcode
  const filtered = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.sku_code ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (p.barcode ?? "").includes(search),
  );

  async function handleDelete(p) {
    if (!window.confirm(`Delete "${p.name}"?\nThis cannot be undone.`)) return;
    const { error } = await supabase.from("products").delete().eq("id", p.id);
    if (error) alert("Error: " + error.message);
    else onRefresh();
  }

  async function handleToggleActive(p) {
    const { error } = await supabase
      .from("products")
      .update({ is_active: !p.is_active })
      .eq("id", p.id);
    if (error) {
      alert("❌ Failed to update product status: " + error.message);
      return;
    }
    onRefresh();
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {/* Search */}
      <div
        className="report-toolbar-row"
        style={{
          padding: "12px 20px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <input
          className="products-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Search by name, SKU or barcode..."
        />
      </div>

      {/* Table */}
      <div className="report-table-wrap">
        {loading ? (
          <div style={{ padding: "16px 20px" }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: "44px",
                  background: "var(--elevated)",
                  borderRadius: "var(--radius)",
                  marginBottom: "8px",
                  animation: "pulse 1.5s ease-in-out infinite",
                  opacity: 1 - i * 0.08,
                }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="report-empty">
            <p>📦</p>
            <p>No products found</p>
          </div>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Name</th>
                <th>Category</th>
                <th>Barcode</th>
                <th>Shelf</th>
                <th>Price (ETB)</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const cat = p.categories;
                return (
                  <tr key={p.id} style={{ opacity: p.is_active ? 1 : 0.45 }}>
                    <td className="receipt-no">{p.sku_code ?? "—"}</td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>
                      {cat ? (
                        <span
                          title={cat.name}
                          style={{
                            display: "inline-block",
                            maxWidth: "140px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            verticalAlign: "bottom",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            fontSize: "11px",
                            background: cat.color + "20",
                            border: `1px solid ${cat.color}40`,
                            color: cat.color,
                          }}
                        >
                          {cat.icon} {cat.name}
                        </span>
                      ) : (
                        <span
                          style={{ color: "var(--text3)", fontSize: "12px" }}
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: "12px",
                        color: "var(--text3)",
                      }}
                    >
                      {p.barcode ?? "—"}
                    </td>
                    <td
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: "12px",
                        color: "var(--purple)",
                      }}
                    >
                      {p.shelf_location ?? "—"}
                    </td>
                    <td className="amount-col">
                      {Number(p.selling_price).toFixed(2)}
                    </td>
                    <td>
                      <span
                        className={
                          p.stock_qty <= 0
                            ? "stock-out"
                            : p.stock_qty <= p.low_stock_threshold
                              ? "stock-low"
                              : "stock-ok"
                        }
                      >
                        {p.stock_qty ?? 0}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: "20px",
                          background: p.is_active
                            ? "var(--green-dim)"
                            : "var(--red-dim)",
                          color: p.is_active ? "var(--green)" : "var(--red)",
                        }}
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button
                          className="edit-btn"
                          title="Edit product"
                          onClick={() => onEdit(p)}
                        >
                          ✏️
                        </button>
                        <button
                          className="edit-btn"
                          title={p.is_active ? "Deactivate" : "Activate"}
                          onClick={() => handleToggleActive(p)}
                          style={{
                            borderColor: p.is_active
                              ? "var(--amber)"
                              : "var(--green)",
                            color: p.is_active
                              ? "var(--amber)"
                              : "var(--green)",
                          }}
                        >
                          {p.is_active ? "⊘" : "✓"}
                        </button>
                        <button
                          className="delete-btn"
                          title="Delete product"
                          onClick={() => handleDelete(p)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   PRODUCT FORM TAB — Add / Edit
============================================================ */
function ProductForm({
  categories,
  products,
  onSaved,
  onCancel,
  editingProduct,
  setEditingProduct,
}) {
  const [form, setForm] = useState(emptyProduct);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load product for editing via editingProduct
  useEffect(() => {
    const p = editingProduct;
    if (p) {
      setEditing(p.id);
      setForm({
        name: p.name ?? "",
        selling_price: p.selling_price ?? "",
        low_stock_threshold: p.low_stock_threshold ?? "5",
        is_active: p.is_active ?? true,
        sku_code: p.sku_code ?? "",
        barcode: p.barcode ?? "", // ✅ included
        category_code: p.category_code ?? "",
        description: p.description ?? "",
        purchase_unit: p.purchase_unit ?? "piece",
        sale_unit: p.sale_unit ?? "piece",
        units_per_purchase: p.units_per_purchase ?? "1",
        category_id: p.category_id ?? "",
        shelf_location: p.shelf_location ?? "",
      });
      // ✅ Clear immediately after reading
      setEditingProduct(null); // ← add this
    }
  }, [editingProduct, setEditingProduct]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError(null);
  }

  function handleReset() {
    setEditing(null);
    setForm(emptyProduct);
    setError(null);
    setEditingProduct(null);
    setEditing(null);
  }

  async function handleSave() {
    // ✅ Validation
    if (!form.name.trim()) {
      setError("Product name is required.");
      return;
    }
    if (!form.selling_price || Number(form.selling_price) <= 0) {
      setError("A valid selling price is required.");
      return;
    }

    setSaving(true);
    setError(null);

    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("store_id")
      .eq("id", (await supabase.auth.getUser()).data.user.id)
      .single();
    logSupabaseError("Products: ProductForm load storeId", profileError);

    const payload = {
      name: form.name.trim(),
      selling_price: Number(form.selling_price),
      low_stock_threshold: Number(form.low_stock_threshold) || 5,
      sku_code: form.sku_code.trim() || null,
      barcode: form.barcode.trim() || null,
      category_code: form.category_code.trim() || null,
      description: form.description.trim() || null,
      purchase_unit: form.purchase_unit || "piece",
      sale_unit: form.sale_unit || "piece",
      units_per_purchase: Number(form.units_per_purchase) || 1,
      category_id: form.category_id || null,
      shelf_location: form.shelf_location.trim() || null,
      is_active: true,
      store_id: profileData?.store_id,
    };

    if (editing) {
      // ✅ Track selling price changes for analytics
      const existing = products.find((p) => p.id === editing);
      const oldPrice = Number(existing?.selling_price ?? 0);
      const newPrice = Number(payload.selling_price);

      const { error: updateError } = await supabase
        .from("products")
        .update(payload)
        .eq("id", editing);

      if (updateError) {
        setError("Update failed: " + updateError.message);
        setSaving(false);
        return;
      }

      // ✅ Log price history if price changed
      if (oldPrice > 0 && oldPrice !== newPrice) {
        const { error: priceHistError } = await supabase.from("price_history").insert({
          product_id: editing,
          price_type: "selling",
          old_price: oldPrice,
          new_price: newPrice,
          reason: "manual update",
          store_id: profileData?.store_id,
        });
        if (priceHistError) {
          alert("❌ Product saved, but failed to log price history: " + priceHistError.message);
          setSaving(false);
          return;
        }
      }
    } else {
      // ✅ Auto-generate SKU
      if (!payload.sku_code) {
        const code = payload.category_code || "PRD";
        payload.sku_code = `${code}-${Date.now().toString().slice(-4)}`;
      }

      const { data: newProduct, error: insertError } = await supabase
        .from("products")
        .insert(payload)
        .select()
        .single();

      if (insertError) {
        setError("Insert failed: " + insertError.message);
        setSaving(false);
        return;
      }

      if (newProduct) {
        // ✅ Log initial price
        const { error: priceHistError } = await supabase.from("price_history").insert({
          product_id: newProduct.id,
          price_type: "selling",
          old_price: null,
          new_price: Number(payload.selling_price),
          reason: "initial",
          store_id: profileData?.store_id,
        });
        if (priceHistError) {
          alert("❌ Product created, but failed to log initial price: " + priceHistError.message);
          setSaving(false);
          return;
        }

        // ✅ Create warehouse stock entry
        const { error: wsError } = await supabase.from("warehouse_stock").insert({
          product_id: newProduct.id,
          quantity: 0,
          store_id: profileData?.store_id,
        });
        if (wsError) {
          alert("❌ Product created, but failed to initialize warehouse stock: " + wsError.message);
          setSaving(false);
          return;
        }
      }
    }

    setSaving(false);
    handleReset();
    onSaved();
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 24px" }}>
      {/* Editing indicator */}
      {editing && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 14px",
            background: "var(--cyan-dim)",
            border: "1px solid rgba(50,212,224,0.3)",
            borderRadius: "var(--radius)",
            marginBottom: "14px",
          }}
        >
          <span
            style={{ fontSize: "13px", color: "var(--cyan)", fontWeight: 600 }}
          >
            ✏️ Editing product
          </span>
          <button
            className="cancel-btn"
            onClick={handleReset}
            style={{ height: "28px", padding: "0 12px", fontSize: "12px" }}
          >
            Clear / New Product
          </button>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div
          style={{
            padding: "10px 14px",
            background: "var(--red-dim)",
            border: "1px solid rgba(255,69,58,0.3)",
            borderRadius: "var(--radius)",
            color: "var(--red)",
            fontSize: "13px",
            marginBottom: "14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>⚠ {error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              color: "var(--red)",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Form grid */}
      <div
        className="form-grid"
        style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: "14px" }}
      >
        {/* ── Identification ── */}
        <div style={groupLabelStyle}>Identification</div>

        <div className="form-field" style={{ gridColumn: "span 2" }}>
          <label style={labelStyle}>Product Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. Coca Cola 500ml"
          />
        </div>

        <div className="form-field">
          <label style={labelStyle}>
            SKU Code{" "}
            <span style={{ color: "var(--text3)", fontSize: "10px" }}>
              (auto if empty)
            </span>
          </label>
          <input
            name="sku_code"
            value={form.sku_code}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. BEV-0001"
          />
        </div>

        <div className="form-field">
          <label style={labelStyle}>Barcode</label>
          <input
            name="barcode"
            value={form.barcode}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. 6291003088485"
          />
        </div>

        {/* ── Classification ── */}
        <div style={groupLabelStyle}>Classification</div>

        <div className="form-field">
          <label style={labelStyle}>Category</label>
          <select
            name="category_id"
            value={form.category_id}
            onChange={(e) => {
              const cat = categories.find((c) => c.id === e.target.value);
              setForm({
                ...form,
                category_id: e.target.value,
                category_code: cat?.category_code ?? form.category_code,
              });
            }}
            style={{ ...inputStyle, cursor: "pointer" }}
          >
            <option value="">No category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name} ({cat.category_code})
              </option>
            ))}
          </select>
        </div>

        <div className="form-field" style={{ gridColumn: "span 2" }}>
          <label style={labelStyle}>
            Description{" "}
            <span style={{ color: "var(--text3)", fontSize: "10px" }}>
              (optional)
            </span>
          </label>
          <input
            name="description"
            value={form.description}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Optional product description..."
          />
        </div>

        {/* ── Pricing & Units ── */}
        <div style={groupLabelStyle}>Pricing & Units</div>

        <div className="form-field">
          <label style={labelStyle}>Selling Price (ETB) *</label>
          <input
            type="number"
            name="selling_price"
            value={form.selling_price}
            onChange={handleChange}
            style={inputStyle}
            placeholder="0.00"
            min="0.01"
            step="0.01"
          />
        </div>

        <div className="form-field">
          <label style={labelStyle}>Purchase Unit</label>
          <input
            name="purchase_unit"
            value={form.purchase_unit}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. crate, box, kg"
          />
        </div>

        <div className="form-field">
          <label style={labelStyle}>Sale Unit</label>
          <input
            name="sale_unit"
            value={form.sale_unit}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. piece, gram"
          />
        </div>

        <div className="form-field">
          <label style={labelStyle}>Units per Purchase</label>
          <input
            type="number"
            name="units_per_purchase"
            value={form.units_per_purchase}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. 24"
            min="1"
          />
        </div>

        {/* ── Inventory Control ── */}
        <div style={groupLabelStyle}>Inventory Control</div>

        <div className="form-field">
          <label style={labelStyle}>Low Stock Alert</label>
          <input
            type="number"
            name="low_stock_threshold"
            value={form.low_stock_threshold}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. 5"
            min="0"
          />
        </div>

        <div className="form-field" style={{ gridColumn: "span 2" }}>
          <label style={labelStyle}>Shelf Location</label>
          <input
            name="shelf_location"
            value={form.shelf_location}
            onChange={handleChange}
            style={inputStyle}
            placeholder="e.g. A01 / Row 3"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="form-actions" style={{ marginTop: "22px" }}>
        <button className="save-btn" disabled={saving} onClick={handleSave}>
          {saving
            ? "Saving..."
            : editing
              ? "💾 Update Product"
              : "➕ Add Product"}
        </button>
        <button className="cancel-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   CATEGORIES TAB
============================================================ */
function CategoriesTab({ categories, onRefresh }) {
  const [form, setForm] = useState(emptyCategory);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingStarter, setLoadingStarter] = useState(false);

  function startEdit(cat) {
    setEditing(cat.id);
    // ✅ Only pick known fields — don't spread entire DB object
    setForm({
      name: cat.name,
      category_code: cat.category_code ?? "",
      icon: cat.icon ?? "📦",
      color: cat.color ?? "#00F3FF",
    });
  }

  function cancelEdit() {
    setEditing(null);
    setForm(emptyCategory);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      alert("Category name is required!");
      return;
    }
    setSaving(true);

    if (editing) {
      const { error } = await supabase.from("categories").update(form).eq("id", editing);
      if (error) {
        alert("❌ Failed to update category: " + error.message);
        setSaving(false);
        return;
      }
    } else {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("store_id")
        .eq("id", (await supabase.auth.getUser()).data.user.id)
        .single();
      logSupabaseError("Products: CategoriesTab load storeId", profileError);

      const { error } = await supabase.from("categories").insert({
        ...form,
        store_id: profileData?.store_id,
      });
      if (error) {
        alert("❌ Failed to create category: " + error.message);
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    cancelEdit();
    onRefresh();
  }

  async function handleLoadStarterCategories() {
    setLoadingStarter(true);
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("store_id")
      .eq("id", (await supabase.auth.getUser()).data.user.id)
      .single();
    logSupabaseError("Products: CategoriesTab load starter storeId", profileError);

    const { error } = await supabase.from("categories").upsert(
      STARTER_CATEGORIES.map((cat) => ({
        ...cat,
        store_id: profileData?.store_id,
      })),
      { onConflict: "store_id,name", ignoreDuplicates: true },
    );
    if (error) {
      alert("❌ Failed to load starter categories: " + error.message);
      setLoadingStarter(false);
      return;
    }

    setLoadingStarter(false);
    onRefresh();
  }

  async function handleDelete(cat) {
    if (
      !window.confirm(
        `Delete "${cat.name}"?\nProducts in this category will be uncategorized.`,
      )
    )
      return;
    const { error } = await supabase.from("categories").delete().eq("id", cat.id);
    if (error) {
      alert("❌ Failed to delete category: " + error.message);
      return;
    }
    onRefresh();
  }

  return (
    <div
      className="report-panel-scroll"
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px 24px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      {/* Form */}
      <div className="wh-section">
        <p className="wh-section-title">
          {editing ? "✏️ Edit Category" : "➕ Add Category"}
        </p>

        <div
          className="prd-category-form-row"
          style={{ marginBottom: "14px" }}
        >
          <div className="form-field">
            <label style={labelStyle}>Category Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Beverages"
            />
          </div>

          <div className="form-field">
            <label style={labelStyle}>Category Code</label>
            <input
              value={form.category_code}
              onChange={(e) =>
                setForm({ ...form, category_code: e.target.value })
              }
              style={inputStyle}
              placeholder="e.g. BEV"
            />
          </div>

          <div className="form-field">
            <label style={labelStyle}>Icon (Emoji)</label>
            <input
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              style={inputStyle}
              placeholder="e.g. 🥤"
            />
          </div>

          <div className="form-field">
            <label style={labelStyle}>Color</label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{
                  width: "42px",
                  height: "38px",
                  borderRadius: "10px",
                  border: "1px solid var(--border)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{ ...inputStyle }}
                placeholder="#00F3FF"
              />
            </div>
          </div>
        </div>

        {/* ✅ Live preview */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "14px",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              color: "var(--text3)",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
            }}
          >
            Preview:
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 14px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: 600,
              background: form.color + "20",
              border: `1px solid ${form.color}50`,
              color: form.color,
            }}
          >
            {form.icon} {form.name || "Category Name"}
          </span>
        </div>

        <div className="form-actions">
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editing ? "💾 Update" : "➕ Add Category"}
          </button>
          {editing && (
            <button className="cancel-btn" onClick={cancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Categories list */}
      <div className="wh-section">
        <p className="wh-section-title">
          🏷️ All Categories ({categories.length})
        </p>

        {categories.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <p style={{ color: "var(--text3)", fontSize: "13px", margin: 0 }}>
              No categories yet. Not every store follows the mini-mart
              layout — add your own above, or start from a preset.
            </p>
            <button
              className="save-btn"
              onClick={handleLoadStarterCategories}
              disabled={loadingStarter}
            >
              {loadingStarter
                ? "Loading..."
                : "🏪 Load starter mini-mart categories"}
            </button>
          </div>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>Preview</th>
                <th>Name</th>
                <th>Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id}>
                  <td>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "3px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        background: cat.color + "20",
                        border: `1px solid ${cat.color}40`,
                        color: cat.color,
                      }}
                    >
                      {cat.icon} {cat.name}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{cat.name}</td>
                  <td
                    style={{
                      fontFamily: "var(--mono)",
                      color: "var(--text3)",
                      fontSize: "12px",
                    }}
                  >
                    {cat.category_code ?? "—"}
                  </td>
                  <td>
                    <div className="action-btns">
                      <button
                        className="edit-btn"
                        onClick={() => startEdit(cat)}
                      >
                        ✏️
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(cat)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
